import { PortableText } from "@portabletext/react";
import { Button, ErrorContainer, Heading, IconChevronRight, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { OpportunitiesAvailableResult, OpportunityItem } from "@/api/sanity/queries/getOpportunities";

import { useGetMyAccount } from "@/api/queries";
import useOpportunitiesQuery from "@/api/sanity/queries/getOpportunities";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import { hasAdminRights } from "@/auth/shared";
import LoadingSmall from "@/components/LoadingSmall";
import useAppStore from "@/store/appStateZustand";

const str = (s: string | null): string => s ?? "";

function Opportunities() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount as string,
  });
  const adminRights = hasAdminRights(myAccount?.data);

  const { data: oppsData, isLoading, isSuccess } = useOpportunitiesQuery();
  const { data: siteConfig } = useSiteConfigAuth();
  const allOpportunitiesAvailable = useAppStore(s => s.allOpportunitiesAvailable) as unknown as boolean | null;

  function isOpportunitiesAvailableResult(data: unknown): data is OpportunitiesAvailableResult {
    return typeof data === "object" && data !== null && "opportunities_available" in data;
  }

  const opps: OpportunityItem[] = allOpportunitiesAvailable === true
    ? (Array.isArray(oppsData) ? oppsData : [])
    : (isOpportunitiesAvailableResult(oppsData) ? oppsData.opportunities_available : []);

  const noOpps = siteConfig?.disable_opportunities;

  useEffect(() => {
    if (noOpps) {
      void navigate({ to: "/" });
    }
  }, [noOpps, navigate]);

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <Heading as="h3" className="mb-8">{t("other:browse-opportunities-header", "Browse Opportunities")}</Heading>
      <div className="w-full flex flex-col gap-6">
        {isLoading && <LoadingSmall />}
        {isSuccess && opps.length > 0 ? (
          opps.map((opp: OpportunityItem) => (
            <div key={opp._id} className="flex flex-row overflow-hidden bg-white rounded-md rounded-base shadow-xs">
              <div className="flex flex-col gap-4 flex-1 p-4">
                <div>
                  <Text className="mb-2 text-lg font-semibold">{opp.name}</Text>
                  <div className="html-content">
                    <PortableText value={opp.description} />
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    label={t("other:apply", "Apply")}
                    icon={<IconChevronRight size="md" className="stroke-ink-primary" />}
                    onClick={
                      adminRights
                        ? () =>
                            (opp.url != null)
                              ? window.open(opp.url, "_blank")
                              : void navigate({ to: `/opportunities/apply/${opp._id}` })
                        : undefined
                    }
                    disabled={!adminRights}
                  />
                </div>
              </div>
              <img
                className="hidden object-cover xl:block xl:w-64 xl:shrink-0 self-stretch"
                src={opp.imageUrl ?? undefined}
                alt={opp.altText}
              />
            </div>
          ))
        ) : (
          <div>
            <ErrorContainer message={str(t("other:no-opportunities", "No Opportunities Available"))} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Opportunities;
